import type {
  IFaqCategory,
  IFaqCopy,
  IFaqItem,
  TFaqCategoryId,
  INumberedFaqItem,
} from "@/src/features/faq/types";

/**
 * Static FAQ content — the single source of truth for the FAQ page (`/faq`),
 * the home page teaser and the listing details section. There is no FAQ API;
 * edit the questions here.
 *
 * Question numbering ("01", "02", …) is derived from array order, so keep the
 * order stable — `id` values are used as URL fragments (`/faq#faq-checkin-1`).
 */

/** Category order drives the sidebar and the section order on /faq. */
export const FAQ_CATEGORIES: [IFaqCategory, ...IFaqCategory[]] = [
  {
    id: "checkin",
    label: {
      en: "NID & Check-In Process",
      bn: "ডকুমেন্ট এবং চেক-ইন প্রক্রিয়া",
    },
  },
  {
    id: "apartment",
    label: { en: "Apartment Details", bn: "এপার্টমেন্ট এর বিস্তারিত" },
  },
  {
    id: "payment",
    label: { en: "Payment & Price", bn: "পেমেন্ট এবং প্রাইস সংক্রান্ত" },
  },
  {
    id: "location",
    label: { en: "Location & Accessibility", bn: "লোকেশন এবং সুবিধাসমূহ" },
  },
  { id: "guests", label: { en: "Guests & Visitors", bn: "ভিজিটর সংক্রান্ত" } },
  { id: "food", label: { en: "Food & Delivery", bn: "ফুড ডেলিভারি" } },
  {
    id: "rules",
    label: { en: "Activities & Rules", bn: "এক্টিভিটি সংক্রান্ত বিষয়" },
  },
  {
    id: "flex",
    label: { en: "Booking Flexibility", bn: "বুকিং ফ্লেক্সিবিলিটি" },
  },
];

/** Category the home page teaser leads with; the rest follow in order. */
export const HOME_FAQ_CATEGORY: TFaqCategoryId = "checkin";

/** Category the listing details section leads with; the rest follow in order. */
export const LISTING_FAQ_CATEGORY: TFaqCategoryId = "apartment";

/**
 * How many categories each teaser shows. Both are a taste of the FAQ, not the
 * FAQ — whatever is left over stays on `/faq`, which is the canonical page and
 * the only one carrying `FAQPage` structured data.
 *
 * The home page has a section of its own to fill, so it carries nearly the whole
 * set; the listing section is a footnote at the end of an already long page and
 * stays short.
 */
export const HOME_FAQ_CATEGORY_COUNT = 7;
export const LISTING_FAQ_CATEGORY_COUNT = 4;

const FAQ_ITEMS: IFaqItem[] = [
  {
    id: "checkin-1",
    category: "checkin",
    question: {
      en: "Whom or where do I provide the documents?",
      bn: "আমি কোথায় ডকুমেন্ট দিবো?",
    },
    answer: {
      en: "Every guest is required to provide their NID information in the app after confirming a booking. Please upload both sides of the NID along with a clear individual photo. Kindly note that the host may also request these documents, and you must be prepared to share them if needed.",
      bn: "প্রতিটি গেস্টকে বুকিং নিশ্চিত করার পর অ্যাপে তাঁদের জাতীয় পরিচয়পত্রের (NID) তথ্য সাবমিট করতে হবে। অনুগ্রহ করে NID-এর উভয় দিকের ছবি এবং নিজেদের একটি স্পষ্ট ছবি অ্যাপে আপলোড করুন। দয়া করে মনে রাখবেন, হোস্টও এই ডকুমেন্টগুলো চাইতে পারেন, এবং প্রয়োজনে আপনাকে সেগুলো শেয়ার করার জন্য প্রস্তুত থাকতে হবে।",
    },
  },
  {
    id: "checkin-2",
    category: "checkin",
    question: {
      en: "Can we submit NID while checking in?",
      bn: "আমরা কি চেক-ইন করার সময় NID জমা দিতে পারি?",
    },
    answer: {
      en: "All the required documents must be provided before checking in.",
      bn: "প্রয়োজনীয় সমস্ত ডকুমেন্ট চেক-ইন করার আগেই সরবরাহ করতে হবে।",
    },
  },
  {
    id: "checkin-3",
    category: "checkin",
    question: {
      en: "Will they take pictures while checking in?",
      bn: "চেক-ইন করার সময় কি তারা ছবি তুলবে?",
    },
    answer: {
      en: "The check-in process may vary depending on the host. Guests are required to follow the specific rules and guidelines set for each property.",
      bn: "চেক-ইন প্রক্রিয়া হোস্টের ওপর নির্ভর করে ভিন্ন হতে পারে। গেস্টদের প্রতিটি প্রপার্টির জন্য সেট করা নির্দিষ্ট নিয়ম ও নির্দেশনা মেনে চলতে হবে।",
    },
  },
  {
    id: "checkin-4",
    category: "checkin",
    question: {
      en: "Who will receive us while checking in?",
      bn: "চেক-ইন করার সময় কে আমাদের রিসিভ করবেন?",
    },
    answer: {
      en: "Check-in details will be provided by the host after confirming the booking.",
      bn: "বুকিং নিশ্চিত হওয়ার পর হোস্ট আপনাকে চেক-ইন-এর বিস্তারিত তথ্য জানিয়ে দেবেন।",
    },
  },
  {
    id: "checkin-5",
    category: "checkin",
    question: {
      en: "Do we need to check in together or separately?",
      bn: "আমাদের কি একসাথে চেক-ইন করতে হবে নাকি আলাদাভাবে?",
    },
    answer: {
      en: "The check-in process may vary depending on the host. Guests are required to follow the specific rules and guidelines set for each property. You can check the description box or ask the host through the chatbox prior to the booking.",
      bn: "চেক-ইন প্রক্রিয়া হোস্টের ওপর নির্ভর করে ভিন্ন হতে পারে। গেস্টদের প্রতিটি প্রপার্টির জন্য সেট করা নির্দিষ্ট নিয়ম ও নির্দেশনা মেনে চলতে হবে। আপনি বুকিং করার আগে ডেসক্রিপশন বক্স দেখতে পারেন অথবা চ্যাটবক্সের মাধ্যমে হোস্টের কাছে বিস্তারিত জানতে পারেন।",
    },
  },
  {
    id: "checkin-6",
    category: "checkin",
    question: {
      en: "Can I go out within the check-in and check-out time?",
      bn: "আমি কি চেক-ইন এবং চেক-আউটের সময়ের মধ্যে বাইরে যেতে পারব?",
    },
    answer: {
      en: "Guests are required to follow the specific rules and guidelines set for each property. You can check the description box or ask the host through the chatbox prior to the booking.",
      bn: "গেস্টদের প্রতিটি প্রপার্টির জন্য সেট করা নির্দিষ্ট নিয়ম ও নির্দেশনা মেনে চলতে হবে। আপনি বুকিং করার আগে ডেসক্রিপশন বক্স দেখতে পারেন অথবা চ্যাটবক্সের মাধ্যমে হোস্টের কাছে বিস্তারিত জানতে পারেন।",
    },
  },
  {
    id: "checkin-7",
    category: "checkin",
    question: {
      en: "Who will hand over the keys after checking in?",
      bn: "চেক-ইন করার পর কে চাবি হস্তান্তর করবেন?",
    },
    answer: {
      en: "Check-in details will be provided by the host after confirming the booking.",
      bn: "বুকিং নিশ্চিত হওয়ার পর হোস্ট আপনাকে চেক-ইন-এর বিস্তারিত তথ্য জানিয়ে দেবেন।",
    },
  },
  {
    id: "checkin-8",
    category: "checkin",
    question: {
      en: "Can I check in at midnight?",
      bn: "আমি কি মাঝরাতে চেক-ইন করতে পারব?",
    },
    answer: {
      en: "The check-in and check-out time is mentioned in each listing. Please inform the host of your preferred check-in time through the app chatbox. You may proceed with the booking once the host confirms.",
      bn: "প্রতিটি লিস্টিং-এ চেক-ইন এবং চেক-আউটের সময় উল্লেখ করা আছে। অনুগ্রহ করে অ্যাপের চ্যাটবক্সের মাধ্যমে হোস্টকে আপনার পছন্দের চেক-ইন সময় সম্পর্কে জানান। হোস্ট নিশ্চিত করলে আপনি বুকিং সম্পন্ন করতে পারেন।",
    },
  },
  {
    id: "checkin-9",
    category: "checkin",
    question: {
      en: "Is it available right now? Can I check in now?",
      bn: "এটা কি এখনই খালি আছে? আমি কি এখনই চেক-ইন করতে পারি?",
    },
    answer: {
      en: "Please inform the host of your preferred check-in time through the app chatbox. You may proceed with the booking once the host confirms.",
      bn: "অনুগ্রহ করে অ্যাপের চ্যাটবক্সের মাধ্যমে হোস্টকে আপনার পছন্দের চেক-ইন সময় সম্পর্কে জানান। হোস্ট নিশ্চিত করলে আপনি বুকিং সম্পন্ন করতে পারেন।",
    },
  },
  {
    id: "checkin-10",
    category: "checkin",
    question: {
      en: "What if the host does not pick up my call or scams me?",
      bn: "যদি হোস্ট আমার কল না ধরেন বা প্রতারণা করেন?",
    },
    answer: {
      en: "All of the hosts in the app are verified. You can also check our app for honest reviews. Furthermore, you can reach out to us anytime if you need any assistance.",
      bn: "ট্রাভেলা অ্যাপের সমস্ত হোস্ট ভেরিফায়েড। এছাড়াও, আপনি আমাদের অ্যাপে সৎ রিভিউগুলি দেখতে পারেন। আপনার কোনো সহায়তার প্রয়োজন হলে আপনি যেকোনো সময় আমাদের সাথে যোগাযোগ করতে পারেন।",
    },
  },
  {
    id: "apartment-1",
    category: "apartment",
    question: {
      en: "Is it a full apartment or a private room?",
      bn: "এটা কি পুরো অ্যাপার্টমেন্ট নাকি প্রাইভেট রুম?",
    },
    answer: {
      en: "This specific information is already mentioned in the app and website. You are requested to kindly check the listing details. You can also text the host through the app chatbox in case of any confusion or if you need any additional information.",
      bn: "এই নির্দিষ্ট তথ্যটি অ্যাপে ইতোমধ্যে উল্লেখ করা আছে। অনুগ্রহ করে অ্যাপে লিস্টিং-এর ডেসক্রিপশন বক্স দেখুন। কোনো বিভ্রান্তি থাকলে বা অতিরিক্ত তথ্যের প্রয়োজন হলে আপনি অ্যাপের চ্যাটবক্সের মাধ্যমে হোস্টকে টেক্সট করতে পারেন।",
    },
  },
  {
    id: "apartment-2",
    category: "apartment",
    question: {
      en: "Will there be anyone else in the apartment?",
      bn: "অ্যাপার্টমেন্টে কি অন্য কেউ থাকবে?",
    },
    answer: {
      en: 'If you book the "Entire Place" then there will be no other guests — you will have it all to yourself. If you book a "Private Room" of an apartment then there might be other people in different rooms.',
      bn: 'আপনি যদি "সম্পূর্ণ অ্যাপার্টমেন্ট" (Entire Place) বুক করেন, তবে অন্য কোনো গেস্ট থাকবে না। আপনি পুরোটা একাই ব্যবহার করতে পারবেন। যদি আপনি একটি অ্যাপার্টমেন্টের "প্রাইভেট রুম" বুক করেন, তবে অন্যান্য রুমে অন্য লোক থাকতে পারে।',
    },
  },
  {
    id: "apartment-3",
    category: "apartment",
    question: {
      en: "Is there an AC or attached washroom?",
      bn: "এখানে কি এসি/সংযুক্ত বাথরুম আছে?",
    },
    answer: {
      en: "Please check the listing details in the app or website. You can also ask the host through the chatbox.",
      bn: "অনুগ্রহ করে অ্যাপে লিস্টিং-এর ডেসক্রিপশন বক্স দেখুন। আপনি অ্যাপ চ্যাটবক্সের মাধ্যমে হোস্টকেও জিজ্ঞাসা করতে পারেন।",
    },
  },
  {
    id: "apartment-4",
    category: "apartment",
    question: {
      en: "Are there kitchen facilities available? Can I cook?",
      bn: "রান্নাঘরের সুবিধা কি আছে? আমি কি রান্না করতে পারব?",
    },
    answer: {
      en: "Please check the listing details in the app or website. You can also ask the host through the chatbox.",
      bn: "অনুগ্রহ করে অ্যাপে লিস্টিং-এর ডেসক্রিপশন বক্স দেখুন। আপনি অ্যাপ চ্যাটবক্সের মাধ্যমে হোস্টকেও জিজ্ঞাসা করতে পারেন।",
    },
  },
  {
    id: "apartment-5",
    category: "apartment",
    question: {
      en: "Can I go to the balcony?",
      bn: "আমি কি বারান্দায় যেতে পারব?",
    },
    answer: {
      en: "Please check the listing details and description box in the app or website. You can also ask the host through the app chatbox.",
      bn: "অনুগ্রহ করে অ্যাপে লিস্টিং-এর ডেসক্রিপশন বক্স দেখুন। আপনি অ্যাপ চ্যাটবক্সের মাধ্যমে হোস্টকেও জিজ্ঞাসা করতে পারেন।",
    },
  },
  {
    id: "apartment-6",
    category: "apartment",
    question: {
      en: "Are there any house rules I should be aware of?",
      bn: "আমার জানা দরকার এমন কোনো হাউজ রুলস আছে?",
    },
    answer: {
      en: "Please read the description box carefully where all the house rules are mentioned. You can ask the host through the app chatbox if you want to know anything specific.",
      bn: "অনুগ্রহ করে ডেসক্রিপশন বক্সটি মনোযোগ সহকারে পড়ুন, যেখানে সমস্ত হাউজ রুলস উল্লেখ করা আছে। আপনি যদি নির্দিষ্ট কিছু জানতে চান তবে অ্যাপ চ্যাটবক্সের মাধ্যমে হোস্টকে জিজ্ঞাসা করতে পারেন।",
    },
  },
  {
    id: "apartment-7",
    category: "apartment",
    question: {
      en: "Are pets allowed?",
      bn: "পোষা প্রাণী কি এলাউড?",
    },
    answer: {
      en: "Please check the description box of the listing. You can also ask the host about this through the chatbox.",
      bn: "অনুগ্রহ করে লিস্টিং-এর ডেসক্রিপশন বক্স দেখুন। আপনি অ্যাপ চ্যাটবক্সের মাধ্যমে হোস্টকেও এ বিষয়ে জিজ্ঞাসা করতে পারেন।",
    },
  },
  {
    id: "apartment-8",
    category: "apartment",
    question: {
      en: "Is there a lift or elevator?",
      bn: "এখানে কি লিফট/এলিভেটর আছে?",
    },
    answer: {
      en: "Please check the description box of the listing. You can also ask the host about this through the chatbox.",
      bn: "অনুগ্রহ করে লিস্টিং-এর ডেসক্রিপশন বক্স দেখুন। আপনি অ্যাপ চ্যাটবক্সের মাধ্যমে হোস্টকেও এ বিষয়ে জিজ্ঞাসা করতে পারেন।",
    },
  },
  {
    id: "apartment-9",
    category: "apartment",
    question: {
      en: "Are there any CCTV cameras?",
      bn: "আপনাদের কি সিসিটিভি আছে?",
    },
    answer: {
      en: "This depends on the hosts. The hosts might have CCTV cameras at the main entrance for safety purposes, but we can assure you there are no CCTVs inside the property.",
      bn: "এটি হোস্টের ওপর নির্ভর করে। হোস্টরা নিরাপত্তার উদ্দেশ্যে মূল প্রবেশপথে সিসিটিভি ক্যামেরা রাখতে পারেন, তবে আমরা আপনাকে নিশ্চিত করতে পারি যে প্রপার্টির ভেতরে কোনো সিসিটিভি নেই।",
    },
  },
  {
    id: "apartment-10",
    category: "apartment",
    question: {
      en: "Is there any hidden charge?",
      bn: "কোনো হিডেন চার্জ আছে কি?",
    },
    answer: {
      en: "There is no hidden charge in the app or website. The guest has to pay only the amount shown for the booking. Although the host can charge some extra amount based on extra facilities given such as parking, food etc. Please check the listing details or ask the host through the chatbox.",
      bn: "এখানে কোনো হিডেন চার্জ প্রযোজ্য নয়। গেস্টদের শুধুমাত্র বুকিংয়ের জন্য অ্যাপে দেখানো প্রাইস-ই পে করতে হবে। তবে, পার্কিং, খাবার ইত্যাদির মতো অতিরিক্ত সুবিধার ভিত্তিতে হোস্ট কিছু অতিরিক্ত চার্জ নিতে পারেন। অনুগ্রহ করে লিস্টিং-এর ডেসক্রিপশন বক্স দেখুন অথবা অ্যাপ চ্যাটবক্সের মাধ্যমে হোস্টকে জিজ্ঞাসা করুন।",
    },
  },
  {
    id: "payment-1",
    category: "payment",
    question: {
      en: "Can I pay in cash?",
      bn: "আমি কি নগদে (Cash) টাকা দিতে পারব?",
    },
    answer: {
      en: "Cash payment is not possible. The payment has to be done through the app or website only, via mobile banking (bKash / Nagad / Rocket etc) and bank cards.",
      bn: "ক্যাশ পেমেন্ট সম্ভব নয়। পেমেন্ট অবশ্যই অ্যাপ থেকে মোবাইল ব্যাংকিং (বিকাশ/নগদ/রকেট ইত্যাদি) অথবা ব্যাংক কার্ডের মাধ্যমে করতে হবে।",
    },
  },
  {
    id: "payment-2",
    category: "payment",
    question: {
      en: "Can I pay after checking in?",
      bn: "আমি কি চেক-ইন করার পর পেমেন্ট করতে পারব?",
    },
    answer: {
      en: "The exact location and the check-in details are only provided after confirming the booking. The guest must pay through the app or website and confirm the booking before checking in.",
      bn: "মূল লোকেশন এবং চেক-ইন ডিটেইলস শুধুমাত্র বুকিং কনফার্ম করার পরেই সরবরাহ করা হয়। চেক-ইন এর আগে গেস্টকে অবশ্যই অ্যাপের মাধ্যমে পেমেন্ট করে বুকিং কনফার্ম করতে হবে।",
    },
  },
  {
    id: "payment-3",
    category: "payment",
    question: {
      en: "How can I pay without bKash?",
      bn: "বিকাশ ছাড়া কীভাবে পেমেন্ট করব?",
    },
    answer: {
      en: "The payment can be done through mobile banking — bKash, Nagad, Rocket, Upay — or bank cards.",
      bn: "শুধুমাত্র মোবাইল ব্যাংকিং (বিকাশ/নগদ/রকেট/উপায়) বা ব্যাংক কার্ডের মাধ্যমে পেমেন্ট করা যেতে পারে।",
    },
  },
  {
    id: "payment-4",
    category: "payment",
    question: {
      en: "Can the price be reduced?",
      bn: "দাম কি কমানো যেতে পারে?",
    },
    answer: {
      en: "We already have the best prices on our platform. You can find more apartments and rooms on a friendly budget on our app or website. You can also ask the host for an additional discount.",
      bn: "আমাদের অ্যাপে ডিসকাউন্টেড রেট দেওয়া আছে। আপনি সাশ্রয়ী বাজেটে আরও অ্যাপার্টমেন্ট ও রুম পাবেন আমাদের অ্যাপে। এছাড়াও, আপনি আরো ডিসকাউন্টের জন্য হোস্টকেও জিজ্ঞাসা করতে পারেন।",
    },
  },
  {
    id: "payment-5",
    category: "payment",
    question: {
      en: "Is there any discount available?",
      bn: "কোনো ডিসকাউন্ট আছে কি?",
    },
    answer: {
      en: "Prices are already discounted in our app and website. Please search your preferred area to find your best price.",
      bn: "আমাদের অ্যাপে ডিসকাউন্টেড রেট দেওয়া আছে। আপনি সাশ্রয়ী বাজেটে পছন্দমতো অপশন বেছে নিতে আপনার লোকেশন সার্চ করুন।",
    },
  },
  {
    id: "payment-6",
    category: "payment",
    question: {
      en: "Is there any discount for weekly or monthly stays?",
      bn: "সাপ্তাহিক/মাসিক থাকার জন্য কোনো ডিসকাউন্ট আছে কি?",
    },
    answer: {
      en: "A discount might automatically be applied on long bookings depending on the hosts and their properties. You can also ask the host for an additional discount through the chatbox.",
      bn: "দীর্ঘ মেয়াদী বুকিং-এর ওপর সাধারণত স্বয়ংক্রিয়ভাবে একটি ডিসকাউন্ট প্রয়োগ হয়ে থাকে। এছাড়াও, আপনি অ্যাপ চ্যাটবক্সের মাধ্যমে হোস্টের কাছে আরো ডিসকাউন্টের জন্য অনুরোধ করতে পারেন।",
    },
  },
  {
    id: "payment-7",
    category: "payment",
    question: {
      en: "Can I get an invoice or receipt after payment?",
      bn: "পেমেন্টের পর কি আমি চালান বা রসিদ পেতে পারি?",
    },
    answer: {
      en: 'You can find the booking and payment details in the "Bookings" tab of the app.',
      bn: 'আপনি অ্যাপের "Bookings" ট্যাবে বুকিং এবং পেমেন্টের বিস্তারিত দেখতে পাবেন।',
    },
  },
  {
    id: "payment-8",
    category: "payment",
    question: {
      en: "What is the price?",
      bn: "রেন্ট কত?",
    },
    answer: {
      en: "Pricing varies by area and also depends on the property type. But to give you an idea, our room prices start from 1,550 taka per day.",
      bn: "সাধারণত এলাকা অনুযায়ী এবং প্রোপার্টির ধরন অনুযায়ী ভাড়ার ধরণ ভিন্ন হয়ে থাকে। কিন্তু আমরা একটা ধারণা দিতে পারি যে, আমাদের রুমের রেন্ট দিনপ্রতি ১৫৫০/- টাকা থেকে শুরু হয়ে থাকে।",
    },
  },
  {
    id: "location-1",
    category: "location",
    question: {
      en: "How far is your location from here?",
      bn: "এখান থেকে আপনাদের লোকেশন কত দূরে?",
    },
    answer: {
      en: "Please search your location in the app or website to find the nearest available options. You can check the map of the listing to have an idea about the distance and location, or you can ask the host through the chatbox.",
      bn: "নিকটতম অপশনগুলো দেখতে অ্যাপে আপনার লোকেশন সার্চ করুন। দূরত্ব এবং লোকেশন সম্পর্কে ধারণা পেতে আপনি লিস্টিং-এর ম্যাপ দেখতে পারেন বা অ্যাপের চ্যাটবক্সের মাধ্যমে হোস্টকে জিজ্ঞাসা করতে পারেন।",
    },
  },
  {
    id: "location-2",
    category: "location",
    question: {
      en: "Where is the location?",
      bn: "লোকেশন কোথায়?",
    },
    answer: {
      en: "We are available all over Bangladesh — Dhaka, Chittagong, Rajshahi, Bogura, Sylhet, Barishal, Comilla, Mymensingh, Munshiganj, Khulna, Cox’s Bazar, Kuakata, Saint Martin, Tangail, Faridpur, Chapainawabganj and all major regions. Please search your desired location to find the best available options.",
      bn: "আমরা আছি — ঢাকা, চট্টগ্রাম, রাজশাহী, বগুড়া, সিলেট, বরিশাল, কুমিল্লা, ময়মনসিংহ, মুন্সিগঞ্জ, খুলনা, কক্সবাজার, কুয়াকাটা, সেন্ট মার্টিন, টাঙ্গাইল, ফরিদপুর, চাঁপাইনবাবগঞ্জসহ বাংলাদেশের অধিকাংশ জেলায়। আপনার সেরা অপশনগুলো দেখতে আপনার পছন্দের লোকেশন সার্চ করুন।",
    },
  },
  {
    id: "location-3",
    category: "location",
    question: {
      en: "Give me the house address and exact location.",
      bn: "আমাকে বাসার ঠিকানা ও সঠিক লোকেশন দিন।",
    },
    answer: {
      en: "The exact location can only be provided after confirming the booking. Before that you can see a general location in the app or website, or ask the host for an idea about the location.",
      bn: "মূল লোকেশনটি শুধুমাত্র বুকিং নিশ্চিত করার পরেই দেওয়া যেতে পারে। তার আগে আপনি অ্যাপে একটি সাধারণ লোকেশন দেখতে পারবেন বা লোকেশন সম্পর্কে ধারণা পেতে হোস্টকে জিজ্ঞাসা করতে পারেন।",
    },
  },
  {
    id: "location-4",
    category: "location",
    question: {
      en: "Is parking available there?",
      bn: "এখানে কি পার্কিং সুবিধা আছে?",
    },
    answer: {
      en: "Please check the description box of the listing in the app or website. You can also ask the host through the chatbox.",
      bn: "অনুগ্রহ করে অ্যাপে লিস্টিং-এর ডেসক্রিপশন বক্স দেখুন। আপনি অ্যাপ চ্যাটবক্সের মাধ্যমে হোস্টকেও জিজ্ঞাসা করতে পারেন।",
    },
  },
  {
    id: "location-5",
    category: "location",
    question: {
      en: "Is parking free or paid?",
      bn: "পার্কিং কি বিনামূল্যে নাকি চার্জযুক্ত?",
    },
    answer: {
      en: "Please check the description box of the listing in the app or website. You can also ask the host through the chatbox.",
      bn: "অনুগ্রহ করে অ্যাপে লিস্টিং-এর ডেসক্রিপশন বক্স দেখুন। আপনি অ্যাপ চ্যাটবক্সের মাধ্যমে হোস্টকেও জিজ্ঞাসা করতে পারেন।",
    },
  },
  {
    id: "guests-1",
    category: "guests",
    question: {
      en: "Can 3 people check in?",
      bn: "৩ জন কি চেক-ইন করতে পারবে?",
    },
    answer: {
      en: "This depends on the listing occupancy. On each listing, you can see how many people it can accommodate. Generally, a private room has a guest limit of 2. If you want to add another guest you need to book 2 private rooms or an entire place.",
      bn: "এটি লিস্টিং-এর ধারণক্ষমতার ওপর নির্ভর করে। সাধারণত, একটি প্রাইভেট রুমে সর্বোচ্চ ২ জন গেস্ট থাকতে পারে। আপনি যদি আরও একজন গেস্ট যোগ করতে চান, তবে আপনাকে ২টি প্রাইভেট রুম বা একটি পুরো অ্যাপার্টমেন্ট বুক করতে হবে।",
    },
  },
  {
    id: "guests-2",
    category: "guests",
    question: {
      en: "Are unmarried couples allowed?",
      bn: "অবিবাহিত কাপল কি এলাউড?",
    },
    answer: {
      en: "This completely depends on the host. Travela is a marketplace that connects hosts and guests. You can check in the description box if the host allows any certain type of guests. Note that all guests must be 18+ and must provide an NID or passport copy when they check in.",
      bn: "এটি সম্পূর্ণরূপে হোস্টের ওপর নির্ভর করে। হোস্ট কি কি ধরণের গেস্ট এলাউ করেন তা আপনি ডেসক্রিপশন বক্সে দেখতে পারেন। মনে রাখবেন, গেস্টদের বয়স অবশ্যই ১৮+ হতে হবে এবং উভয় গেস্টকে চেক-ইন করার সময় NID/পাসপোর্টের কপি সাবমিট করতে হবে।",
    },
  },
  {
    id: "guests-3",
    category: "guests",
    question: {
      en: "Are visitors allowed inside the apartment?",
      bn: "অ্যাপার্টমেন্টের ভেতরে কি ভিজিটরদের অনুমতি আছে?",
    },
    answer: {
      en: "Please check the description box of the listing in the app or website. You can also ask the host through the app chatbox.",
      bn: "অনুগ্রহ করে অ্যাপে লিস্টিং-এর ডেসক্রিপশন বক্স দেখুন। আপনি অ্যাপ চ্যাটবক্সের মাধ্যমে হোস্টকেও জিজ্ঞাসা করতে পারেন।",
    },
  },
  {
    id: "food-1",
    category: "food",
    question: {
      en: "Can I order from Foodpanda or any food delivery app?",
      bn: "আমি কি ফুডপান্ডা থেকে অর্ডার করতে পারব?",
    },
    answer: {
      en: "Yes. Generally, you can order food from any food delivery service during your stay. But to be certain you have to check the description box. Note that all the house rules have to be maintained while ordering and receiving the food.",
      bn: "হ্যাঁ। আপনি যেকোনো ফুড ডেলিভারি সার্ভিস থেকে খাবার অর্ডার করতে পারেন। তবে মনে রাখবেন, খাবার অর্ডার এবং রিসিভ করার সময় হাউজ রুলস বজায় রাখতে হবে।",
    },
  },
  {
    id: "rules-1",
    category: "rules",
    question: {
      en: "Can we have a party?",
      bn: "আমরা কি পার্টি করতে পারব?",
    },
    answer: {
      en: "It totally depends on the hosts and their properties whether they allow a party or not. Please review the property description and inform the host in advance before making a booking.",
      bn: "সাধারণত, হোস্টরা তাদের প্রপার্টিতে পার্টির অনুমতি দেন না। বুকিং করার আগে অনুগ্রহ করে ডেসক্রিপশন বক্স দেখুন এবং হোস্টকে আগে থেকেই জানান।",
    },
  },
  {
    id: "rules-2",
    category: "rules",
    question: {
      en: "Is smoking allowed?",
      bn: "ধূমপান করা যাবে?",
    },
    answer: {
      en: "Please check the description box of the listing in the app or website. You can also ask the host through the chatbox.",
      bn: "অনুগ্রহ করে অ্যাপে লিস্টিং-এর ডেসক্রিপশন বক্স দেখুন। আপনি অ্যাপ চ্যাটবক্সের মাধ্যমে হোস্টকেও জিজ্ঞাসা করতে পারেন।",
    },
  },
  {
    id: "flex-1",
    category: "flex",
    question: {
      en: "Can we extend the check-out time as we are checking in late?",
      bn: "আমরা দেরিতে চেক-ইন করবো, তাই কি চেক-আউট সময় বাড়ানো যাবে?",
    },
    answer: {
      en: "We are sorry, but late check-in does not guarantee a late check-out. The check-out time must be followed as per the property policy. In any case of emergency please check with the host if they can allow it.",
      bn: "প্রপার্টির রুলস অনুযায়ী চেক-আউটের সময়টি অবশ্যই মেনে চলতে হবে। কোনো জরুরি অবস্থার ক্ষেত্রে অনুগ্রহ করে হোস্টের সাথে কথা বলে দেখুন তিনি অনুমতি দিতে পারেন কিনা।",
    },
  },
  {
    id: "flex-2",
    category: "flex",
    question: {
      en: "Can I visit the apartment before booking?",
      bn: "বুকিং করার আগে কি আমি অ্যাপার্টমেন্টটি দেখতে যেতে পারি?",
    },
    answer: {
      en: "Visiting the apartment before booking is not allowed. You can refer to the listing details and photos for all necessary information. You can also ask the host through the app chatbox in any case of confusion.",
      bn: "বুকিং করার আগে অ্যাপার্টমেন্ট পরিদর্শনে যাওয়ার অনুমতি নেই। প্রয়োজনীয় সমস্ত তথ্যের জন্য আপনি লিস্টিং-এর বিস্তারিত এবং ছবিগুলো দেখতে পারেন। কোনো নির্দিষ্ট প্রশ্ন থাকলে আপনি অ্যাপের চ্যাটবক্সের মাধ্যমে হোস্টকেও জিজ্ঞাসা করতে পারেন।",
    },
  },
  {
    id: "flex-3",
    category: "flex",
    question: {
      en: "I want to come to your office for booking.",
      bn: "আমি বুকিংয়ের জন্য আপনাদের অফিসে আসতে চাই।",
    },
    answer: {
      en: "We do not encourage office visits as the booking process is entirely online. Hence, bookings cannot be made at the office. All bookings must be completed through the app or website only.",
      bn: "বুকিং সংক্রান্ত কোনো সেবা অফিসে প্রদান করা হয় না। তাই একটি অনলাইন বুকিং মাধ্যম হিসেবে আমরা অফিসে এসে বুকিং করার জন্য নিরুৎসাহিত করি। সমস্ত বুকিং প্রক্রিয়া শুধুমাত্র অ্যাপের মাধ্যমেই সম্পন্ন করতে হবে।",
    },
  },
  {
    id: "flex-4",
    category: "flex",
    question: {
      en: "Can I check in early?",
      bn: "আমি কি আগে চেক-ইন করতে পারি?",
    },
    answer: {
      en: "The check-in time must be followed as per the property policy. Although you can ask the host through the chatbox and check in early only if the host allows.",
      bn: "প্রপার্টির নিয়ম অনুযায়ী চেক-ইন টাইম অবশ্যই মেনে চলতে হবে। তবে, আপনি চ্যাটবক্সের মাধ্যমে হোস্টকে জিজ্ঞাসা করতে পারেন এবং যদি হোস্ট অনুমতি দেন তবেই আগে চেক-ইন করতে পারবেন।",
    },
  },
];

/** UI copy for both languages. */
export const FAQ_COPY: Record<"en" | "bn", IFaqCopy> = {
  en: {
    kicker: "Before you book",
    homeTitle: "Questions guests ask us most",
    seeMore: "See all FAQs",
    chatUs: "Chat with support",
    homeSub:
      "Check-in, documents, payment and house rules — the short answers. Everything else is in the full FAQ.",
    faqCrumb: "FAQ",
    faqTitle: "Frequently asked questions",
    faqSub:
      "Everything about booking, checking in, paying and staying — grouped by topic. Switch to বাংলা any time.",
    searchPh: "Search questions — NID, parking, discount…",
    categories: "Categories",
    all: "All questions",
    stillTitle: "Still stuck?",
    stillSub: "Our support team replies in the app, usually within minutes.",
    emptyTitle: "No matching questions",
    emptySub:
      "Try a shorter search, or a different category. Support can always help directly.",
    listingFaqTitle: "Good to know about this stay",
    listingFaqSub:
      "Common questions for this listing. Anything specific — message the host.",
    resultOf: "of",
    questions: "QUESTIONS",
  },
  bn: {
    kicker: "বুকিং করার আগে",
    homeTitle: "গেস্টরা যা সবচেয়ে বেশি জিজ্ঞাসা করেন",
    seeMore: "সব প্রশ্ন দেখুন",
    chatUs: "সাপোর্টে চ্যাট করুন",
    homeSub:
      "চেক-ইন, ডকুমেন্ট, পেমেন্ট আর হাউজ রুলস — সংক্ষেপে উত্তর। বাকি সব প্রশ্ন পুরো FAQ-তে।",
    faqCrumb: "সাধারণ প্রশ্ন",
    faqTitle: "সাধারণ জিজ্ঞাসা",
    faqSub:
      "বুকিং, চেক-ইন, পেমেন্ট আর থাকা নিয়ে সব কিছু — বিষয় অনুযায়ী সাজানো। যেকোনো সময় English-এ যেতে পারেন।",
    searchPh: "প্রশ্ন খুঁজুন — NID, পার্কিং, ডিসকাউন্ট…",
    categories: "ক্যাটাগরি",
    all: "সব প্রশ্ন",
    stillTitle: "উত্তর পাননি?",
    stillSub: "আমাদের সাপোর্ট টিম অ্যাপে সাধারণত কয়েক মিনিটেই উত্তর দেয়।",
    emptyTitle: "কোনো প্রশ্ন মেলেনি",
    emptySub:
      "ছোট শব্দে খুঁজুন, বা অন্য ক্যাটাগরি দেখুন। সাপোর্ট সবসময় সাহায্য করতে পারে।",
    listingFaqTitle: "এই স্টে সম্পর্কে জেনে নিন",
    listingFaqSub:
      "এই লিস্টিং নিয়ে সাধারণ প্রশ্ন। নির্দিষ্ট কিছু হলে হোস্টকে মেসেজ করুন।",
    resultOf: "/",
    questions: "প্রশ্ন",
  },
};

/**
 * Every FAQ item with its display number. Numbering runs across the whole list
 * (not per category), so a category's questions keep the same numbers wherever
 * they are rendered — home teaser, listing section or `/faq`.
 */
export const NUMBERED_FAQ_ITEMS: INumberedFaqItem[] = FAQ_ITEMS.map(
  (item, index) => ({ ...item, num: String(index + 1).padStart(2, "0") }),
);

export const FAQ_TOTAL_COUNT = NUMBERED_FAQ_ITEMS.length;
